// Eskom PARCC — theme (dark/light) provider + hook.
//
// A real light mode alongside an optional dark look. The active theme is an
// attribute on <html> (`data-theme`), so all CSS tokens flip at once (see
// styles/theme.css). The reader's choice persists in localStorage; the DEFAULT is
// light (TK's call — light is the default surface, dark is opt-in), NOT the OS
// preference. An inline <head> script in index.html applies the same resolution
// synchronously to avoid a flash; this provider hydrates from the identical
// source so React and the DOM agree.
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'eskom-parcc:theme';

/**
 * Resolve the initial theme: a previously-stored choice wins; otherwise light
 * (the product default — dark is opt-in). Pure + side-effect-free so it is
 * unit-testable; the caller supplies the stored value (so we don't couple the
 * logic to localStorage).
 */
export function resolveInitialTheme(stored: string | null | undefined): Theme {
  return stored === 'light' || stored === 'dark' ? stored : 'light';
}

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

interface ThemeCtx {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [theme, setThemeState] = useState<Theme>(() => resolveInitialTheme(readStored()));

  // Keep <html data-theme> and localStorage in sync with state.
  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore quota / private-mode failures */
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggle = useCallback(() => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')), []);

  return <Ctx.Provider value={{ theme, toggle, setTheme }}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
